import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Modal, Table, Alert, Tag, Progress } from 'antd';
import { toast, Bounce } from 'react-toastify';
import { Button } from 'components/Button';
import {
  getUniformItemsApi,
  getPositionMappingsApi,
  createPositionMappingApi,
  uniformErrorMessage,
} from 'api/services/uniformManagement';
import { uniformGenderOptions } from '../../constants';
import useStickyIdentity from '../../components/useStickyIdentity';

const toastOpts = { position: 'top-right', autoClose: 4000, transition: Bounce };

/** Uniform ITEM gender (M/F/U) — not an employee's gender. */
const itemGenderLabel = (code) => uniformGenderOptions.find((g) => g.value === code)?.label || 'Unisex';

const itemGenderColor = (code) => (code === 'M' ? 'blue' : code === 'F' ? 'magenta' : 'default');

// Above this many mappings in one go, confirm first — see handleSave.
const BULK_CONFIRM_THRESHOLD = 15;

/** Per-item failure text. Extraction stays with uniformErrorMessage; this only
 *  recognises the one case with a hostile default: a mapping created by
 *  someone else between our load and our POST trips the (uniform_item,
 *  position) unique constraint, which DRF renders as "The fields uniform_item,
 *  position must make a unique set." */
const mappingErrorMessage = (err) => {
  const message = uniformErrorMessage(err, 'Could not be mapped');
  return /must make a unique set/i.test(message)
    ? 'Already mapped to this position — someone else added it just now'
    : message;
};

/**
 * Creates UniformPosition master-data rows (uniform item -> position) inline
 * from either issuance screen, so the "no uniforms mapped to <Position>" dead
 * end is fixable without leaving for Masters -> Position Mapping.
 *
 * Deliberately position-scoped, never employee-scoped: a mapping applies to
 * every employee holding the position, so the dialog names only the position
 * and says so before the user confirms.
 *
 * @param {object}   position  { id, name } — the position being mapped. Required.
 * @param {function} onSaved   ({ createdItemIds, position }) — fired whenever at
 *   least one mapping was created, including on partial failure, so the caller
 *   can reload its employee/item data while the dialog stays open on what failed.
 */
const PositionUniformMappingModal = ({ open, position, onClose, onSaved }) => {
  // Hook form, not static Modal.confirm: the static call renders outside the
  // React tree and so never sees the app's ConfigProvider theme (index.jsx),
  // rendering unthemed. Its holder sits inside this Modal's children below, so
  // antd's zIndex context stacks these confirms above their parent dialog.
  const [modal, modalContextHolder] = Modal.useModal();
  const [items, setItems] = useState([]);
  const [mappedIds, setMappedIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [saving, setSaving] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  // Outcome of the last run: { succeededCount, failed[], notAttempted[], aborted }.
  // Everything the summary claims is read from here, so a run that mapped
  // nothing can never render a sentence saying something was mapped.
  const [runResult, setRunResult] = useState(null);
  const [refreshToken, setRefreshToken] = useState(0);

  // "Latest request wins" guard, covering the loading reset too, so a slow
  // response for a position the user has since closed/reopened away from can't
  // repaint this dialog with another position's mappings.
  const reqIdRef = useRef(0);
  // Flipped by Stop (or Esc/X/mask during a run). Checked at the top of every
  // iteration so the loop breaks between requests, never mid-flight.
  const abortRef = useRef(false);
  // Callers derive `open` from the position itself, so the prop goes null the
  // moment the dialog starts closing. The id stays live (nothing may be
  // fetched or written for a position that is no longer selected) but the
  // NAME falls back to the last one seen, so the title doesn't flicker to a
  // generic placeholder through the close animation.
  const lastPositionRef = useRef(null);
  if (position) lastPositionRef.current = position;

  const positionId = position?.id ?? null;
  const positionName = (position || lastPositionRef.current)?.name || 'this position';

  // Clear per-open state up front — including the rows themselves. This
  // component stays mounted between opens (destroyOnHidden only tears down the
  // Modal's inner content), so without clearing `items`/`mappedIds` here a
  // reopen for a different position paints one frame of the PREVIOUS
  // position's catalogue and mapped-state under the new position's name.
  // `loading` is set in the same commit rather than left to the fetch effect,
  // so that frame shows the spinner instead.
  useEffect(() => {
    if (!open) return;
    setSelectedRowKeys([]);
    setRunResult(null);
    setProgress({ done: 0, total: 0 });
    setItems([]);
    setMappedIds([]);
    setLoadError('');
    setLoading(!!positionId);
    abortRef.current = false;
  }, [open, positionId]);

  useEffect(() => {
    if (!open || !positionId) return undefined;
    const reqId = ++reqIdRef.current;
    setLoading(true);
    setLoadError('');
    Promise.all([
      getUniformItemsApi({ page_size: 1000 }),
      getPositionMappingsApi({ position: positionId }),
    ]).then(([itemsRes, mappingsRes]) => {
      if (reqId !== reqIdRef.current) return;
      // Only active items can be issued (IssuanceEmployeesView filters mapped
      // items on status/deleted), so mapping an inactive one would create a
      // row that never reaches a single employee.
      const rows = (itemsRes.data?.results || [])
        .filter((i) => i.status !== false)
        .map((i) => ({ id: i.id, name: i.name, image: i.image, gender: i.gender, sizeCount: (i.sizes || []).length }));
      const mappings = mappingsRes.data?.data || [];
      const nextMappedIds = mappings.map((m) => m.uniform_item?.id ?? m.uniform_item).filter((id) => id != null);
      setItems(rows);
      setMappedIds(nextMappedIds);
      // Drop anything that turns out to be mapped already from the selection —
      // after a retry-refresh, an item that failed *because* someone else had
      // just mapped it would otherwise sit checked but disabled.
      const mapped = new Set(nextMappedIds);
      setSelectedRowKeys((prev) => prev.filter((id) => !mapped.has(id)));
    }).catch((e) => {
      if (reqId !== reqIdRef.current) return;
      setItems([]);
      setMappedIds([]);
      const message = uniformErrorMessage(e, 'Failed to load uniform items for this position');
      setLoadError(message);
      toast.error(message, toastOpts);
    }).finally(() => {
      if (reqId === reqIdRef.current) setLoading(false);
    });
    return undefined;
  }, [open, positionId, refreshToken]);

  // Unmapped first (the only actionable rows), each block alphabetical — with a
  // large catalogue the few addable items would otherwise be lost among the
  // already-mapped ones.
  const rows = useMemo(() => {
    const mapped = new Set(mappedIds);
    return items
      .map((i) => ({ ...i, mapped: mapped.has(i.id) }))
      .sort((a, b) => (
        a.mapped !== b.mapped
          ? (a.mapped ? 1 : -1)
          : (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' })
      ));
  }, [items, mappedIds]);

  const mappedCount = rows.filter((r) => r.mapped).length;
  const availableCount = rows.length - mappedCount;

  // The Stop button: an unambiguous, deliberate gesture, so it takes effect at
  // once. The loop breaks before its next request; anything already written
  // stays and is reported.
  const stopRun = () => { abortRef.current = true; };

  // Esc, the X and the mask land here instead. Everywhere else in the app those
  // mean "dismiss, no consequence" — here they would halt a sequence of master
  // data writes — so during a run they ask first rather than silently stopping.
  // The dialog is still never locked: this is a question, not a block, and the
  // queue keeps running behind it until the user answers.
  const handleCancel = () => {
    if (!saving) { onClose(); return; }
    modal.confirm({
      title: `Stop mapping uniforms to ${positionName}?`,
      centered: true,
      content: `${progress.done} of ${progress.total} done. Whatever has already been mapped stays; the rest will not be attempted.`,
      okText: 'Stop',
      cancelText: 'Keep mapping',
      // Safe if the run finishes while this is open: runMappings clears
      // abortRef before its loop, so a late stop can't bleed into a later run.
      onOk: stopRun,
    });
  };

  /** One POST per mapping (the endpoint takes a single pair), run strictly in
   *  sequence — never an unbounded parallel fan-out — and reporting exactly
   *  which ones landed, so a partial success is neither dressed up as a full
   *  success nor thrown away as a total failure.
   *
   *  The queue is abortable between requests (see abortRef): the header
   *  select-all can tick every unmapped item at once, and a long run must
   *  never become something the user is locked into. */
  const runMappings = async (targets) => {
    setSaving(true);
    setRunResult(null);
    setProgress({ done: 0, total: targets.length });
    abortRef.current = false;

    const succeeded = [];
    const failed = [];
    let stoppedAt = targets.length;
    for (let i = 0; i < targets.length; i += 1) {
      // Checked before each request, so a stop takes effect between calls and
      // never leaves a half-sent write behind.
      if (abortRef.current) { stoppedAt = i; break; }
      const row = targets[i];
      try {
        // Awaited inside the loop on purpose: strictly one request at a time.
        await createPositionMappingApi({ uniform_item: row.id, position: positionId });
        succeeded.push(row);
      } catch (err) {
        failed.push({ id: row.id, name: row.name, message: mappingErrorMessage(err) });
      }
      setProgress((prev) => ({ ...prev, done: prev.done + 1 }));
    }

    const aborted = stoppedAt < targets.length;
    const notAttempted = targets.slice(stoppedAt);
    setSaving(false);
    abortRef.current = false;

    // Anything that landed is real and stays — tell the caller to reload even
    // when the rest failed or was never attempted.
    if (succeeded.length > 0) {
      onSaved?.({ createdItemIds: succeeded.map((r) => r.id), position });
    }

    // Clean sweep: nothing failed and nothing was skipped.
    if (failed.length === 0 && !aborted) {
      toast.success(
        `${succeeded.length} uniform${succeeded.length === 1 ? '' : 's'} mapped to ${positionName}`,
        toastOpts
      );
      onClose();
      return;
    }

    // Otherwise stay open with an account of the run. Refresh so whatever
    // landed shows as already mapped, and leave the unfinished items selected
    // so continuing is one click.
    setRunResult({
      succeededCount: succeeded.length,
      failed,
      notAttempted,
      aborted,
    });
    setSelectedRowKeys([...failed.map((f) => f.id), ...notAttempted.map((r) => r.id)]);
    setRefreshToken((t) => t + 1);

    if (failed.length > 0) {
      toast.error(
        succeeded.length > 0
          ? `Mapped ${succeeded.length} of ${targets.length} — ${failed.length} failed`
          : `None of the ${targets.length} selected uniforms could be mapped`,
        toastOpts
      );
    } else {
      toast.warning(
        `Stopped after mapping ${succeeded.length} of ${targets.length}`,
        toastOpts
      );
    }
  };

  const handleSave = () => {
    const targets = rows.filter((r) => !r.mapped && selectedRowKeys.includes(r.id));
    // No position, nothing to map to — a mapping POST without one would be
    // rejected anyway, and silently writing against a stale id would be worse.
    if (!positionId || targets.length === 0) return;

    // One request per mapping means a select-all on a large catalogue is a long
    // run against master data. Name both costs before it starts; it can still
    // be stopped part-way once running.
    if (targets.length > BULK_CONFIRM_THRESHOLD) {
      modal.confirm({
        title: `Map ${targets.length} uniforms to ${positionName}?`,
        centered: true,
        content: `Every employee holding ${positionName} becomes eligible for all ${targets.length}. They are created one at a time, so this will take a moment — you can stop it part-way, and anything already created stays.`,
        okText: `Map ${targets.length}`,
        onOk: () => runMappings(targets),
      });
      return;
    }
    runMappings(targets);
  };

  const pinLeft = useStickyIdentity();

  const columns = [
    {
      // Pinned, with `rowSelection.fixed` below. antd puts the checkbox at
      // index 0, so pinning this column alone would leave a gap and antd
      // would drop the lane's shadow; fixing the selection column keeps the
      // run contiguous and keeps the tick beside the name it applies to.
      title: 'Uniform Item',
      dataIndex: 'name',
      ...pinLeft(240, { maxWidth: 200 }),
      render: (name, row) => (
        <div className="d-flex align-items-center gap-2">
          {row.image && (
            <img src={row.image} alt="" style={{ width: 28, height: 28, objectFit: 'cover', borderRadius: 4 }} />
          )}
          <span className={row.mapped ? 'text-muted' : ''}>{name}</span>
        </div>
      ),
    },
    {
      title: 'Gender',
      dataIndex: 'gender',
      render: (gender) => <Tag color={itemGenderColor(gender)}>{itemGenderLabel(gender)}</Tag>,
    },
    {
      title: 'Sizes',
      dataIndex: 'sizeCount',
      render: (count) => (count > 0 ? count : <span className="text-muted">Standard</span>),
    },
    {
      title: 'Status',
      key: 'status',
      render: (_, row) => (row.mapped
        ? <Tag color="green">Already mapped</Tag>
        : <span className="text-muted">Not mapped</span>),
    },
  ];

  const rowSelection = {
    // Pins the checkbox gutter with the item name — see the column above.
    fixed: true,
    selectedRowKeys,
    onChange: setSelectedRowKeys,
    getCheckboxProps: (row) => ({
      disabled: row.mapped || saving,
      title: row.mapped ? `Already mapped to ${positionName}` : undefined,
    }),
  };

  const selectedCount = selectedRowKeys.length;

  return (
    <Modal
      title={`Assign uniforms to ${positionName}`}
      open={open}
      onCancel={handleCancel}
      width={760}
      centered
      // Deliberately still closable during a run — the user is never locked in
      // behind the queue. Esc/X/mask route to handleCancel, which asks before
      // stopping rather than halting master-data writes on a stray gesture.
      maskClosable
      closable
      destroyOnHidden
      footer={
        <div className="d-flex justify-content-between align-items-center">
          <small className="text-muted">
            {saving
              ? `Mapping ${progress.done} of ${progress.total}…`
              : (selectedCount > 0 ? `${selectedCount} selected` : '')}
          </small>
          <div className="d-flex gap-2">
            <Button type="button" className="btn btn-outline-secondary btn-sm"
              label={saving ? 'Stop' : 'Cancel'} onClick={saving ? stopRun : handleCancel} />
            <Button type="button" className="btn btn-primary btn-sm" loading={saving}
              disabled={saving || loading || !positionId || selectedCount === 0}
              label={selectedCount > 0
                ? `Map ${selectedCount} uniform${selectedCount === 1 ? '' : 's'} to ${positionName}`
                : 'Map to position'}
              onClick={handleSave} />
          </div>
        </div>
      }
    >
      {modalContextHolder}

      <Alert
        type="warning"
        showIcon
        className="mb-3"
        message="This updates company master data"
        description={
          <span>
            Uniforms added here are mapped to the position <strong>{positionName}</strong>. Every
            employee holding that position — now and in future, across every cost center — becomes
            eligible for them. This is not limited to the employee you were issuing to.
          </span>
        }
      />

      {loadError && (
        <Alert type="error" showIcon className="mb-3" message={loadError} />
      )}

      {saving && (
        <div className="mb-3">
          <div className="d-flex justify-content-between align-items-center mb-1">
            <span style={{ fontSize: 13 }}>Mapping {progress.done} of {progress.total} to {positionName}…</span>
            <small className="text-muted">Stop to end after the current one — anything already mapped stays.</small>
          </div>
          <Progress
            percent={progress.total ? Math.round((progress.done / progress.total) * 100) : 0}
            status="active"
            size="small"
          />
        </div>
      )}

      {runResult && (runResult.failed.length > 0 || runResult.aborted) && (
        <Alert
          type={runResult.failed.length > 0 ? 'error' : 'warning'}
          showIcon
          className="mb-3"
          message={runResult.failed.length > 0
            ? `${runResult.failed.length} uniform${runResult.failed.length === 1 ? '' : 's'} could not be mapped`
            : 'Stopped before finishing'}
          description={
            <>
              {runResult.failed.length > 0 && (
                <ul className="mb-1 ps-3">
                  {runResult.failed.map((f) => <li key={f.id}>{f.name}: {f.message}</li>)}
                </ul>
              )}
              {/* Every claim below is read from the run's own tallies. In
                  particular the "were mapped" sentence is gated on something
                  actually having been mapped — an all-fail run must never
                  imply a write that never happened. */}
              <small className="text-muted d-block">
                {runResult.succeededCount > 0
                  ? `${runResult.succeededCount} uniform${runResult.succeededCount === 1 ? ' was' : 's were'} mapped and now show as already mapped below.`
                  : 'Nothing was mapped.'}
                {runResult.notAttempted.length > 0
                  && ` ${runResult.notAttempted.length} ${runResult.notAttempted.length === 1 ? 'was' : 'were'} not attempted.`}
              </small>
              {(runResult.failed.length > 0 || runResult.notAttempted.length > 0) && (
                <small className="text-muted d-block mt-1">
                  Still selected below, so you can try again.
                </small>
              )}
            </>
          }
        />
      )}

      {!loading && rows.length > 0 && (
        <div className="text-muted mb-2" style={{ fontSize: 12.5 }}>
          {mappedCount} of {rows.length} active uniform items are already mapped to {positionName}
          {availableCount === 0 && ' — there is nothing left to add.'}
        </div>
      )}

      <Table
        rowKey={(r) => r.id}
        rowSelection={rowSelection}
        columns={columns}
        dataSource={rows}
        loading={loading}
        pagination={false}
        size="small"
        scroll={{ x: 'max-content', y: 320 }}
        locale={{ emptyText: loadError ? 'Could not load uniform items' : 'No active uniform items — add one under Masters → Uniform Items' }}
      />

      <small className="text-muted d-block mt-2">
        Employees only receive mapped items that match their own gender; Unisex items reach everyone
        in the position.
      </small>
    </Modal>
  );
};

export default PositionUniformMappingModal;
